/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { forwardRef, useEffect, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';

import Portal from '@atlaskit/portal';
import type { SpotlightContainerProps } from './types';

type Props = SpotlightContainerProps & React.RefAttributes<HTMLDivElement>;

const styles = cssMap({
	root: {
		transitionProperty: 'opacity',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: '200ms',
	},
	hidden: {
		opacity: 0,
	},
	visible: {
		opacity: 1,
	},
});

export const TransitionGroup = forwardRef<HTMLDivElement, Omit<Props, 'showSpotlight'>>(
	({ containerStyles, zIndex, ariaLabelledBy, shouldBlink, stopBlink, ...rest }, ref) => {
		const [opacityStyle, setOpacityStyle] = useState<'hidden' | 'visible'>('hidden');

		useEffect(() => {
			setTimeout(() => {
				// to get transition to work properly
				setOpacityStyle(!shouldBlink ? 'visible' : 'hidden');
			}, 0);

			if (shouldBlink) {
				setTimeout(() => {
					stopBlink();
				}, 200);
			}
		}, [shouldBlink, stopBlink]);

		return (
			<Portal zIndex={zIndex}>
				<div
					ref={ref}
					css={[styles.root, styles[opacityStyle]]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={containerStyles}
					aria-labelledby={ariaLabelledBy}
					{...rest}
				/>
			</Portal>
		);
	},
);

export const Transition = forwardRef<HTMLDivElement, Props>(
	(
		{ containerStyles, zIndex, ariaLabelledBy, shouldBlink, showSpotlight, stopBlink, ...rest },
		ref,
	) => {
		return (
			showSpotlight && (
				<TransitionGroup
					containerStyles={containerStyles}
					ref={ref}
					ariaLabelledBy={ariaLabelledBy}
					stopBlink={stopBlink}
					shouldBlink={shouldBlink}
					zIndex={zIndex}
					{...rest}
				/>
			)
		);
	},
);
